import React from 'react';
import { Box, Container, Typography, Button, Grid, Paper, Card, CardContent } from '@mui/material';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PlaceIcon from '@mui/icons-material/Place';
import ShareLocationIcon from '@mui/icons-material/ShareLocation';
import ExploreIcon from '@mui/icons-material/Explore';
import PeopleIcon from '@mui/icons-material/People';
import LockOpenIcon from '@mui/icons-material/LockOpen';

function LandingPage({ onLoginClick, onSignupClick }) {
  const features = [
    {
      icon: <NoteAddIcon sx={{ fontSize: 60 }} />,
      title: 'Drop Notes',
      description: 'Leave digital notes at real-world locations for others to discover',
    },
    {
      icon: <PlaceIcon sx={{ fontSize: 60 }} />,
      title: 'Location-Based',
      description: 'Notes are tied to specific geographic coordinates, creating a location-aware experience',
    },
    {
      icon: <ShareLocationIcon sx={{ fontSize: 60 }} />,
      title: 'Discover Nearby',
      description: 'Find notes that others have left in your area as you explore',
    },
    {
      icon: <ExploreIcon sx={{ fontSize: 60 }} />,
      title: 'Explore the World',
      description: 'Turn any location into a canvas for thoughts, stories, and messages',
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 60 }} />,
      title: 'Connect Locally',
      description: 'Share experiences with people who have been where you are',
    },
    {
      icon: <LockOpenIcon sx={{ fontSize: 60 }} />,
      title: 'Open & Simple',
      description: 'No complicated features, just drop a note and move on',
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        color: 'text.primary',
      }}
    >
      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 8, md: 12 },
          pb: { xs: 8, md: 12 },
          background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h1"
              sx={{
                fontFamily: 'Gochi Hand, cursive',
                fontSize: { xs: '3rem', md: '5rem', lg: '6rem' },
                fontWeight: 700,
                mb: 3,
                background: 'linear-gradient(45deg, #ffffff 30%, #cccccc 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              dropped.ink
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                color: 'text.secondary',
                maxWidth: '700px',
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Leave your mark on the world. Drop notes at real locations and discover what others have left behind.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={onSignupClick}
                sx={{
                  bgcolor: 'white',
                  color: 'black',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: '#f0f0f0',
                  },
                }}
              >
                Sign Up Now
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={onLoginClick}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#f0f0f0',
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                Log In
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Typography
          variant="h2"
          sx={{
            textAlign: 'center',
            mb: 6,
            fontFamily: 'Gochi Hand, cursive',
            fontSize: { xs: '2rem', md: '3rem' },
          }}
        >
          How It Works
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  bgcolor: 'background.paper',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 8px 24px rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Box
                    sx={{
                      color: 'white',
                      mb: 2,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      fontFamily: 'Gochi Hand, cursive',
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(135deg, #1a1a1a 0%, #000000 100%)',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              sx={{
                mb: 3,
                fontFamily: 'Gochi Hand, cursive',
                fontSize: { xs: '2rem', md: '2.5rem' },
              }}
            >
              Ready to Start Dropping?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
              Join the community and leave your mark on the world
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={onSignupClick}
              sx={{
                bgcolor: 'white',
                color: 'black',
                px: 5,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#f0f0f0',
                },
              }}
            >
              Get Started Free
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default LandingPage;
